'use client';

import Link from 'next/link';
import { ResearchCTA } from '@/components/cta/ResearchCTA';
import { ctaPropsFor } from '@/lib/cta';
import { useEffect } from 'react';

/**
 * 404 page with telemetry. Fires a `page_not_found` custom event to GA4 +
 * Vercel Analytics so we can catch missed-URL patterns (e.g., the truncated
 * slug that cost us most of the Research Library's indexing through the
 * v3.0 window). Review weekly in GA4: Reports → Engagement → Events.
 */

export default function NotFound() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname + window.location.search;
    const referrer = document.referrer || '(none)';
    const payload = { page_path: path, referrer };
    // `gtag` + `dataLayer` are declared by the GA4 <Script> in app/layout.tsx.
    // @vercel/analytics also declares a Window.gtag shape, so we read loosely
    // rather than add a conflicting global augmentation.
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    if (typeof w.gtag === 'function') {
      w.gtag('event', 'page_not_found', payload);
    } else if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: 'page_not_found', ...payload });
    }
  }, []);

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-10 py-20">
      <div className="text-center max-w-[640px]">
        <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-aviado-teal-text mb-5">
          404 · Not Found
        </div>
        <h1
          className="font-display font-medium text-warm-navy m-0 mb-5 text-[clamp(32px,4vw,48px)] leading-[1.05] tracking-[-0.02em]"
          style={{ fontVariationSettings: '"opsz" 48, "SOFT" 50' }}
        >
          We couldn&rsquo;t find that page.
        </h1>
        <p className="font-sans text-[17px] leading-[1.55] text-slate max-w-[52ch] mx-auto mb-9">
          The article may have moved, been retired in the v3.0 redesign, or you may
          have landed on a truncated URL. The Research Library has 158+ peer-reviewed
          studies — try the catalog or head back to the home page.
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link
            href="/"
            className="inline-flex items-center px-5 py-3 rounded-md bg-warm-navy text-white font-ui text-[14px] font-medium no-underline hover:bg-aviado-blue transition-colors duration-fast"
          >
            Research Library home
          </Link>
          <Link
            href="/browse"
            className="inline-flex items-center px-5 py-3 rounded-md bg-white text-warm-navy font-ui text-[14px] font-medium no-underline border border-border hover:border-border-strong transition-colors duration-fast"
          >
            Browse all studies
          </Link>
        </div>

        {/* Recovery capture — dead-ends are a recurring GSC 404 class; give
            the lost visitor a conversion path alongside the internal links
            above (which stay — they're the recovery + crawl path). */}
        <div className="mt-10 text-left">
          <ResearchCTA
            {...ctaPropsFor({
              context: 'generic',
              slug: 'not-found',
              topicName: 'Aviado Research',
            })}
            variant="compact"
          />
        </div>
      </div>
    </div>
  );
}
